import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import React, { useEffect } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { useLogoFlutter } from '@/assets/icons/useLogoFlutter';
import {
  type BaseModalFCProps,
  ModalsManager,
} from '@/components/modals/modalsManager';
import { YStack, Button } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { useHashRoute } from '@/hooks/useHashRoute';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { PLAN_FEATURES } from './subscription.constants';
import { useSubscription } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import { SubscriptionPlan } from './subscription.types';

const LOGO_SIZE = 28;

type TitleProps = {
  children: React.ReactNode;
  logo?: React.ReactNode;
};

const Title = ({
  children,
  logo = <AppLogo size={LOGO_SIZE} decorative />,
}: TitleProps) => {
  return (
    <YStack className="gap-5">
      {logo}
      <Dialog.Title className="font-serif text-[1.6875rem] font-normal leading-tight tracking-normal text-pretty">
        {children}
      </Dialog.Title>
    </YStack>
  );
};

type SubtitleProps = {
  children: React.ReactNode;
};

const Subtitle = ({ children }: SubtitleProps) => {
  return (
    <Dialog.Description className="text-[0.9375rem] leading-normal text-pretty">
      {children}
    </Dialog.Description>
  );
};

type PlanFeatureListProps = {
  features: string[];
};

const PlanFeatureList = ({ features }: PlanFeatureListProps) => {
  return (
    <ul className="flex flex-col gap-2.5">
      {features.map((feature) => (
        <li
          key={feature}
          className="flex gap-2.5 text-sm leading-snug text-foreground text-pretty"
        >
          <span className="flex h-[1lh] shrink-0 items-center">
            <Icons.check className="size-3.5 text-foreground-muted" />
          </span>
          {feature}
        </li>
      ))}
    </ul>
  );
};

type DowngradeModalProps = BaseModalFCProps;

export const DowngradeModal = observer(
  ({ open, onOpenChange }: DowngradeModalProps) => {
    const user = userStore.user;
    const { setHash } = useHashRoute();
    const trialEnded = subscriptionStore.state.endedInTrial;

    const handleUpgrade = () => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'downgrade_modal' });
      userStore.syncUpdate({
        feature_flags: {
          ...user?.feature_flags,
          show_downgrade_modal: false,
        },
      });
      setHash('settings/Subscription');
      onOpenChange(false);
    };

    const handleClose = () => {
      userStore.syncUpdate({
        feature_flags: {
          ...user?.feature_flags,
          show_downgrade_modal: false,
        },
      });
      onOpenChange(false);
    };

    return (
      <Dialog.Root open={open} onOpenChange={() => {}}>
        <Dialog.Content
          className="max-w-md rounded-2xl"
          showCloseButton={false}
        >
          <Dialog.Header className="text-left">
            <Title>
              {trialEnded ? 'Your Plus trial has ended' : "You're on Basic now"}
            </Title>
            <Subtitle>
              {trialEnded
                ? 'Pick a plan any time to get it back.'
                : 'Your paid plan has ended. You can choose a new one any time.'}
            </Subtitle>
          </Dialog.Header>
          <Dialog.Footer className="mt-2">
            <Button variant="ghost" onClick={handleClose}>
              Not now
            </Button>
            <Button onClick={handleUpgrade}>See plans</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);

type UpgradeModalProps = {
  title?: string;
  description?: string;
  onClose?: () => void;
} & BaseModalFCProps;

export const UpgradeModal = observer(
  ({
    title = 'Upgrade your plan',
    description = "Here's what Plus adds.",
    open,
    onOpenChange,
    onClose,
  }: UpgradeModalProps) => {
    const { setHash } = useHashRoute();

    const handleDismiss = (isOpen: boolean) => {
      if (!isOpen) {
        onClose?.();
      }
      onOpenChange(isOpen);
    };

    const handleUpgrade = () => {
      trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'upgrade_modal' });
      setHash('settings/Subscription');
      handleDismiss(false);
    };

    return (
      <Dialog.Root open={open} onOpenChange={handleDismiss}>
        <Dialog.Content
          className="max-w-md rounded-2xl"
          showCloseButton={false}
        >
          <Dialog.Header className="text-left">
            <Title>{title}</Title>
            <Subtitle>{description}</Subtitle>
          </Dialog.Header>
          <PlanFeatureList
            features={PLAN_FEATURES[SubscriptionPlan.PLUS_MONTHLY]}
          />
          <Dialog.Footer className="mt-2">
            <Button variant="ghost" onClick={() => handleDismiss(false)}>
              Not now
            </Button>
            <Button onClick={handleUpgrade}>See plans</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);

type UpgradeSuccessModalProps = BaseModalFCProps;

export const UpgradeSuccessModal = observer(
  ({ open, onOpenChange }: UpgradeSuccessModalProps) => {
    const user = userStore.user;
    const { bodyY, wingAngle, playFlutter } = useLogoFlutter();

    const handleClose = () => {
      userStore.syncUpdate({
        feature_flags: {
          ...user?.feature_flags,
          show_upgrade_modal: false,
        },
      });
      onOpenChange(false);
    };

    return (
      <Dialog.Root open={open} onOpenChange={() => {}}>
        <Dialog.Content
          className="max-w-md rounded-2xl"
          showCloseButton={false}
          onOpenAutoFocus={playFlutter}
        >
          <Dialog.Header className="text-left">
            <Title
              logo={
                <motion.div className="self-start" style={{ y: bodyY }}>
                  <AppLogo size={LOGO_SIZE} wingAngle={wingAngle} decorative />
                </motion.div>
              }
            >
              Welcome to {subscriptionStore.currentPlanLabel}
            </Title>
            <Subtitle>
              {subscriptionStore.isPowerOrPro
                ? 'Your plan is active. It includes everything in Plus, and:'
                : "Your plan is active. Here's what it adds."}
            </Subtitle>
          </Dialog.Header>
          <PlanFeatureList
            features={PLAN_FEATURES[subscriptionStore.currentPlan]}
          />
          <Dialog.Footer className="mt-2">
            <Button onClick={handleClose}>Continue</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);

type SignupPlusTrialModalProps = BaseModalFCProps;

export const SignupPlusTrialModal = observer(
  ({ open, onOpenChange }: SignupPlusTrialModalProps) => {
    const user = userStore.user;
    const { bodyY, wingAngle, playFlutter } = useLogoFlutter();
    const trialEnd = subscriptionStore.state.currentPeriodEnd;

    const handleClose = () => {
      userStore.syncUpdate({
        feature_flags: {
          ...user?.feature_flags,
          show_signup_plus_trial_modal: false,
        },
      });
      onOpenChange(false);
    };

    return (
      <Dialog.Root open={open} onOpenChange={() => {}}>
        <Dialog.Content
          className="max-w-md rounded-2xl"
          showCloseButton={false}
          onOpenAutoFocus={playFlutter}
        >
          <Dialog.Header className="text-left">
            <Title
              logo={
                <motion.div className="self-start" style={{ y: bodyY }}>
                  <AppLogo size={LOGO_SIZE} wingAngle={wingAngle} decorative />
                </motion.div>
              }
            >
              Enjoy Plus free for 14 days
            </Title>
            <Subtitle>
              {trialEnd
                ? `Your trial ends ${new Date(trialEnd * 1000).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}. `
                : 'Your trial ends in 14 days. '}
              Add a card any time to keep Plus after that.
            </Subtitle>
          </Dialog.Header>
          <PlanFeatureList
            features={PLAN_FEATURES[SubscriptionPlan.PLUS_MONTHLY]}
          />
          <Dialog.Footer className="mt-2">
            <Button onClick={handleClose}>Start exploring</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);

export const SubscriptionModalsManager = observer(
  function SubscriptionModalsManager() {
    const user = userStore.user;
    const { status, isLoading } = useSubscription();
    const isSubscriptionActive = subscriptionStore.isActive;
    const { setHash } = useHashRoute();

    useEffect(() => {
      const isOnOnboardingRoute = window.location.href.includes('/onboarding');
      if (!user || isLoading || isOnOnboardingRoute) return;

      // UpgradeModal logic
      const shouldShowUpgradeModal =
        user.feature_flags?.show_upgrade_modal && isSubscriptionActive;

      if (shouldShowUpgradeModal) {
        trackEvent('SUBSCRIPTION_UPGRADE_MODAL_SHOWN');
        ModalsManager.show(UpgradeSuccessModal, {}, { unique: true });
      }

      if (
        user.feature_flags?.show_signup_plus_trial_modal &&
        status.subscription_status === 'trialing'
      ) {
        ModalsManager.show(SignupPlusTrialModal, {}, { unique: true });
      }

      // DowngradeModal logic
      const shouldShowDowngradeModal = user.feature_flags?.show_downgrade_modal;

      if (shouldShowDowngradeModal) {
        trackEvent('SUBSCRIPTION_DOWNGRADE_MODAL_SHOWN');
        ModalsManager.show(DowngradeModal, {}, { unique: true });
      }

      const shouldShowManageSubscriptionModal =
        user.feature_flags?.show_manage_subscription_modal;

      if (shouldShowManageSubscriptionModal) {
        setHash('settings/Subscription');
        userStore.syncUpdate({
          feature_flags: {
            ...user?.feature_flags,
            show_manage_subscription_modal: false,
          },
        });
      }
    }, [user, isLoading, status, setHash, isSubscriptionActive]);

    return null;
  }
);
